











































































































import { computed, defineComponent, PropType, ref, toRef, watch, watchEffect } from "@vue/composition-api";
import { FileData } from "@scrile/api-provider/dist/api/FileProvider";
import { TagInfo } from "@scrile/api-provider/dist/api/TagsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import useDarkModeController from "@/hooks/useDarkModeController";
import { darkModeReplacePostfix } from "@/lib/darkModeReplacePostfix";
import { $t } from "@/lib/i18nHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { getEditableQuestionaryInfoFields } from "@/lib/questionaryFieldsHelper";
import { getProfileUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { FormFields, FormValues } from "@/types";
import { ROLES } from "@/const";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseButton from "@/components/BaseButton";
import BaseForm from "@/components/BaseForm";
import BasePage from "@/components/BasePage";
import BaseSpinner from "@/components/BaseSpinner";
import PageTitle from "@/components/PageTitle";
import ProfileHeaderAvatar from "@/components/ProfileHeaderAvatar";
import ProfileHeaderBackground from "@/components/ProfileHeaderBackground";
import SafeForWorkStatusSettings from "@/components/SafeForWorkStatusSettings";
import SelectUserTagsModal from "@/components/SelectUserTagsModal";
import AvatarCropModal from "./AvatarCropModal.vue";
import BannerCropModal from "./BannerCropModal.vue";
import ImageUpload from "./ImageUpload.vue";
import PersonalEditLocalization from "./PersonalEditLocalization.vue";
import ProfileTagsView from "./ProfileTagsView.vue";
import { IFileUploadingType, ProfileFileType } from "../PageProfileEdit.vue";

export default defineComponent({
  name: "PageProfileEditView",
  components: {
    BaseSpinner,
    PersonalEditLocalization,
    AvatarCropModal,
    BannerCropModal,
    SelectUserTagsModal,
    ProfileTagsView,
    ImageUpload,
    ProfileHeaderAvatar,
    ProfileHeaderBackground,
    BaseForm,
    BasePage,
    BaseIcon,
    PageTitle,
    BaseButton,
    SafeForWorkStatusSettings,
  },
  props: {
    user: { type: Object as PropType<UserPublicData> },
    info: { type: Object as PropType<Record<string, string>> },
    files: { type: Array as PropType<FileData[]> },
    loading: { type: Boolean },
    saving: { type: Boolean },
    userTags: { type: Array as PropType<TagInfo[]> },
    fileLoading: { type: Object as PropType<IFileUploadingType> },
    canUpdateLocalization: { type: Boolean, default: true },
  },
  setup(props, { root, emit }) {
    const { isDarkMode } = useDarkModeController();
    const { isMember } = useAuthUser();

    const showUserTagsModal = ref(false);
    const showAvatarCropModal = ref(false);
    const showBannerCropModal = ref(false);
    const isValid = ref(true);
    const newAvatarFile = ref<File | null>();
    const newAvatarSrc = ref("");
    const newBannerFile = ref<File | null>();
    const newBannerSrc = ref("");

    const hasBecomeCreator = computed(
      () => isMember.value && !config.featureSoloModel && !config.featureHideBecomeCreator
    );
    const restrictedMember = computed(
      () => config.featureRestrictMember && checkRole(props.user?.role ?? "", ROLES.MEMBER)
    );
    const isUserCreator = computed(() => checkRole(props.user?.role ?? "", ROLES.CREATOR));

    const showLocalization = computed(() => !config.featureHideLocalization && props.canUpdateLocalization);
    const showSafeForWorkSetup = computed(() => config.featureNSFW && isUserCreator.value);
    const isAvatarLoading = computed(
      () => props.fileLoading?.type === ProfileFileType.AVATAR && props.fileLoading.loading
    );
    const isBackgroundLoading = computed(
      () => props.fileLoading?.type === ProfileFileType.BACKGROUND && props.fileLoading.loading
    );

    const translateLabelsInFields = (fields: FormFields): FormFields => {
      return fields.map(i => ({ ...i, label: i.label && $t(i.label) }));
    };

    const questionaryUserFields = computed(() => {
      const fields = getEditableQuestionaryInfoFields().filter(
        i => !i.fieldMeta?.social && !i.fieldMeta?.amazon && !i.fieldMeta?.calendar
      );
      return translateLabelsInFields(fields);
    });

    const socialFields = computed<FormFields>(() => {
      const fields = restrictedMember.value ? [] : getEditableQuestionaryInfoFields().filter(i => i.fieldMeta?.social);
      return translateLabelsInFields(
        fields.map(i =>
          i.appendIcon ? { ...i, appendIcon: darkModeReplacePostfix(i.appendIcon, isDarkMode.value) } : i
        )
      );
    });

    const amazonFields = computed<FormFields>(() => {
      const fields = isUserCreator.value ? getEditableQuestionaryInfoFields().filter(i => i.fieldMeta?.amazon) : [];
      return translateLabelsInFields(fields);
    });

    const calendarFields = computed<FormFields>(() => {
      const fields = isUserCreator.value ? getEditableQuestionaryInfoFields().filter(i => i.fieldMeta?.calendar) : [];
      return translateLabelsInFields(fields);
    });

    const userFields = computed<FormFields>(() => {
      const fields = restrictedMember.value
        ? questionaryUserFields.value.filter(i => i.fieldMeta?.allowedForMember)
        : questionaryUserFields.value;
      return [
        {
          name: "screenName",
          label: $t("Name"),
          rules: ["required", "max:30"],
          maxlength: "30",
        },
        {
          name: "username",
          label: $t("Username"),
          rules: {
            required: true,
            regex: /^[a-zA-Z0-9_.-]*$/,
            max: 30,
          },
          maxlength: "30",
          rulesMessages: {
            regex: $t("Names can only contain letters, numbers, dashes, dots, and underscores."),
          },
          hint: $t("Names can only contain letters, numbers, dashes, dots, and underscores."),
        },
        ...fields,
      ];
    });

    const getValues = (fields: FormFields) =>
      fields.reduce<FormValues>((out, item) => {
        out[item.name] = props.info ? props.info[item.name] : "";
        return out;
      }, {});

    const userFieldsValues = ref<FormValues>({});
    const socialFieldsValues = ref<FormValues>({});
    const amazonValues = ref<FormValues>({});
    const calendarValues = ref<FormValues>({});

    const unwatch = watch([toRef(props, "loading")], () => {
      userFieldsValues.value = {
        screenName: props.user?.screenName,
        username: props.user?.username,
        ...getValues(questionaryUserFields.value),
      };

      socialFieldsValues.value = getValues(socialFields.value);
      amazonValues.value = getValues(amazonFields.value);
      calendarValues.value = getValues(calendarFields.value);
      unwatch();
    });

    const localizationValues = ref<FormValues>({});

    const allowThemes = computed(() => config.featureTags);

    const editLocalization = (value: FormValues) => {
      if (value.localizationCountryCode) {
        localizationValues.value.localizationCity = value.localizationCity;
        localizationValues.value.localizationCountryCode = value.localizationCountryCode;
        localizationValues.value.localizationStateCode = value.localizationStateCode;
      }

      if (value.localizationLanguage) {
        localizationValues.value.localizationLanguage = value.localizationLanguage;
      }
    };

    const getImageUrl = (type: string) => {
      const file = props.files?.find(file => file.fileId.type === type && file.fileId.subjectId === props.user?.id);
      return file ? MediaFilesController.getImageThumbnail(file.urlPart, "1080p") : "";
    };

    const background = computed(() => getImageUrl(ProfileFileType.BACKGROUND));

    const onBackClick = () => {
      props.user && root.$router.push(getProfileUrl(props.user));
    };

    const onUserTagsSave = (tags: string[]) => {
      showUserTagsModal.value = false;
      emit("updateUserTags", tags);
    };

    const onValidation = (valid: boolean) => {
      isValid.value = valid;
    };

    const onAvatarUploadClick = (fileList: FileList) => {
      newAvatarFile.value = fileList[0];
      showAvatarCropModal.value = true;
    };

    const onAvatarCropClose = () => {
      showAvatarCropModal.value = false;
      newAvatarFile.value = null;
      newAvatarSrc.value = "";
    };

    const onAvatarCropped = (file: File) => {
      emit("upload", [file], ProfileFileType.AVATAR, () => (showAvatarCropModal.value = false));
    };

    const onBannerUploadClick = (fileList: FileList) => {
      newBannerFile.value = fileList[0];
      showBannerCropModal.value = true;
    };

    const onBannerCropClose = () => {
      showBannerCropModal.value = false;
      newBannerFile.value = null;
      newBannerSrc.value = "";
    };

    const onBannerCropped = (file: File) => {
      emit("upload", [file], ProfileFileType.BACKGROUND, () => (showBannerCropModal.value = false));
    };

    const avatarFileReader = new FileReader();
    avatarFileReader.onload = event => {
      if (event && event.target) {
        newAvatarSrc.value = event.target.result as string;
      }
    };

    const bannerFileReader = new FileReader();
    bannerFileReader.onload = event => {
      if (event && event.target) {
        newBannerSrc.value = event.target.result as string;
      }
    };

    watchEffect(() => {
      if (newAvatarFile.value) {
        avatarFileReader.readAsDataURL(newAvatarFile.value);
      } else {
        newAvatarFile.value = null;
      }
    });

    watchEffect(() => {
      if (newBannerFile.value) {
        bannerFileReader.readAsDataURL(newBannerFile.value);
      } else {
        newBannerFile.value = null;
      }
    });

    const save = () => {
      emit("save", {
        ...userFieldsValues.value,
        ...socialFieldsValues.value,
        ...amazonValues.value,
        ...calendarValues.value,
        ...localizationValues.value,
      });
    };

    return {
      isValid,
      restrictedMember,
      isDarkMode,
      hasBecomeCreator,
      userFieldsValues,
      amazonValues,
      calendarValues,
      socialFieldsValues,
      userFields,
      socialFields,
      amazonFields,
      calendarFields,
      showUserTagsModal,
      allowThemes,
      background,
      showAvatarCropModal,
      showBannerCropModal,
      newAvatarSrc,
      newBannerSrc,
      localizationValues,
      showLocalization,
      showSafeForWorkSetup,
      isAvatarLoading,
      isBackgroundLoading,
      onAvatarCropClose,
      onBannerCropClose,
      onBackClick,
      onAvatarUploadClick,
      onBannerUploadClick,
      onAvatarCropped,
      onBannerCropped,
      onUserTagsSave,
      onValidation,
      save,
      editLocalization,
      ProfileFileType,
    };
  },
});
